import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * The last thing between a thrown render and a blank page.
 *
 * Every route but the home page is a React.lazy import behind one Suspense
 * boundary, and Suspense catches a pending promise, not a rejected one. The
 * ordinary way that rejects is a deploy: a visitor holding the old document
 * follows a link, the browser asks for a content-hashed chunk that no longer
 * exists, the import rejects, and with nothing to catch it React unmounts the
 * tree. The visitor is left on white, with no error, nothing to click, and a
 * page that looks broken rather than busy.
 *
 * The fallback is deliberately plain HTML with no imports of its own: a
 * boundary that needs a chunk to render its own error cannot render its own
 * error. Reloading is the actual repair, because the fresh document asks for
 * the new chunk names, so that is the first action and it is a real link, not
 * a handler, so it works even if nothing further can run.
 */
type Props = { children: ReactNode };
type State = { failed: boolean; chunk: boolean };

// A rejected dynamic import reads differently in every engine, so match on
// the shapes rather than one string. Chrome and Safari both say "dynamically
// imported module", Firefox says "error loading dynamically imported module",
// and bundlers raise ChunkLoadError by name.
function looksLikeMissingChunk(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: string }).name ?? "";
  const message = (error as { message?: string }).message ?? "";
  return (
    name === "ChunkLoadError" ||
    /dynamically imported module|importing a module script failed|failed to fetch dynamically/i.test(
      message,
    )
  );
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { failed: false, chunk: false };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, chunk: looksLikeMissingChunk(error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // No reporting endpoint exists and this file is not the place to add one.
    // The console is what a developer reproducing the report will look at.
    console.error("Route render failed", error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="rb-loading" role="alert">
        <p>
          {this.state.chunk
            ? "This page was updated while you had it open."
            : "Something went wrong displaying this page."}
        </p>
        <p>
          <a href={typeof location === "undefined" ? "/" : location.pathname}>
            Reload the page
          </a>{" "}
          or <a href="/">go back to the homepage</a>. You can always reach us on{" "}
          <a href="tel:+18136948671">(813) 694-8671</a>.
        </p>
      </div>
    );
  }
}
